import { el, jsx } from '@amazon/vinyl-tsx'
import type { PlayParams } from '@/service/fragments/playParamsFrag'
import { first, lazy, logWarn, type Maybe, noop, resolveValueProvider } from '@amazon/vinyl-util'
import { createImageModel } from '@/util/ImageUrl'
import { createCloudQueue } from '@/cloudQueue/cloudQueueService'
import { playableEntityToPlaylistEntry } from '@/playback/playableEntityToPlaylistEntry'
import { getLargestImage } from '@/util/imageUtil'
import { ImagePlaceholder } from '@/components/ImagePlaceholder'
import { Icon } from '@/components/Icon'
import { ctx } from '@/util/context'
import { openRecommendationOverflow } from '@/browse/recommendationOverflow'
import { navigateTo } from '@/router/router'
import { setDetailPagePreSeed } from '@/detailpage/setDetailPagePreSeed'
import { ImageView } from '@/components/ImageView'
import { prepareViewTransition, TransitionKeys } from '@/transition/transitionKeys'
import { cacheEntity, type PlayableEntity } from '@/service/fragments/playableEntityFrag'
import type { Player } from '@/playback/api'
import type { ImageFields } from '@/service/fragments/imageFieldsFrag'
import type { EntityUnion } from '@/service/fragments/entity'
import type { CloudQueueController } from '@/cloudQueue/CloudQueueController'
import type { ManagedPlaylistController } from '@/playback/PlaylistController'
import { getAuth } from '@/auth/authRef'
import { createPlayQueueBinding, PlayQueueState } from '@/playback/playQueueBinding'

const { target } = ctx('RecommendationRenderer')

export interface RecommendationModel {
    readonly id: Maybe<string>

    /**
     * Set on playable entities
     */
    readonly playParams: PlayParams | null
    readonly image: ImageFields
    readonly type: RecommendationType
    readonly title: string
    readonly artistName?: Maybe<string>
    readonly link?: Maybe<string>
    readonly node: PlayableEntity
}

export type RecommendationType =
    | 'Album'
    | 'Artist'
    | 'Station'
    | 'Playlist'
    | 'Track'
    | 'PodcastEpisode'
    | 'PodcastShow'
    | 'Audiobook'
    | 'Video'

export interface RecommendationRendererDeps {
    readonly playerPromise: Promise<Player>
    readonly playlistController: ManagedPlaylistController
    readonly cloudQueueController: CloudQueueController
}

export interface RecommendationRendererProps {
    readonly data: RecommendationModel
    readonly imageSize: number
    readonly deps: RecommendationRendererDeps
}

export function RecommendationRenderer(props: RecommendationRendererProps) {
    const { data, deps } = props
    const { errorHandler } = ctx('RecommendationRenderer')

    // Videos render with a 16:9 card; sizing the image to that aspect
    // gives the CDN the right dimensions and avoids letterboxing.
    const resizedImage =
        data.type === 'Video'
            ? createImageModel(data.image, {
                  scaleToWidth: props.imageSize,
                  scaleToHeight: Math.round((props.imageSize * 9) / 16),
              })
            : createImageModel(data.image, {
                  scaleToSquare: props.imageSize,
              })

    const playParams = data.playParams
    const queue =
        // Videos don't go through the audio cloud queue — clicking one just
        // navigates to /videos/:id where the native player takes over.
        // Tracks/Stations/Playlists/etc. still build a queue.
        data.type === 'Video' || playParams == null || !getAuth().isSignedIn()
            ? null
            : lazy(async () => {
                  const result = await createCloudQueue({
                      playParams,
                  })
                  const queue = result.createQueue?.queue
                  if (queue?.id) {
                      const edges = queue.current?.edges
                      const edge = first(edges)
                      const curr = playableEntityToPlaylistEntry(
                          edge?.node as Maybe<PlayableEntity>
                      )
                      if (curr) {
                          // trackLoadOptions can be lazy — resolve
                          // before handing to the player.
                          Promise.all([
                              deps.playerPromise,
                              resolveValueProvider(curr.trackLoadOptions),
                          ])
                              .then(([player, options]) => player.preload(options))
                              .catch(noop)
                      }
                  }
                  return result
              })

    function play() {
        if (data.type === 'Video' && data.id) {
            // Video clicks set a single-track video queue directly — no
            // navigation. The video stage shows via the transport when
            // current is a video. The /videos/:id page is reserved for
            // sharing / deep-link.
            playVideoFromRecommendation(data, deps).catch(errorHandler)
            return
        }
        // If this recommendation is the active queue, the overlay is
        // acting as a pause toggle — hand off to the player rather
        // than re-creating the same queue.
        if (isCurrentlyPlaying?.value) {
            deps.playerPromise
                .then((player) => player.pause())
                .catch(errorHandler)
            return
        }
        if (playParams && queue) {
            queue.value
                .then((queueResult) => {
                    deps.cloudQueueController.setQueue({
                        playParams,
                        queueResult,
                    })
                })
                .catch(errorHandler)
        }
    }

    function navigate() {
        if (!data.link) {
            // No detail destination (Track, Station, Video) — fall back
            // to play, mirroring GH's "card click plays when there's
            // nowhere to go" behavior.
            play()
            return
        }
        const imageContainer = el('.imageContainer', view)
        prepareViewTransition(imageContainer, TransitionKeys.MAIN_IMAGE, data.id)
        const link = data.link
        cacheEntity(data.node)
        setDetailPagePreSeed({ mainImage: resizedImage })
        navigateTo(link)
    }

    const isPlayable = playParams != null || data.type === 'Video'

    // Observe this recommendation's queue state so the overlay can
    // swap play ↔ pause + so CSS can render the equalizer / tint the
    // title on match. Videos don't route through PlayQueueBinding
    // (they use the video queue), so they always show "play".
    const queueBinding =
        isPlayable && data.type !== 'Video'
            ? createPlayQueueBinding({ playParams, deps })
            : null
    const playState = queueBinding
        ? queueBinding.state.map((s) =>
              s === PlayQueueState.PLAYING
                  ? 'playing'
                  : s === PlayQueueState.PAUSED
                    ? 'paused'
                    : null
          )
        : null
    const isCurrentlyPlaying = queueBinding
        ? queueBinding.state.map((s) => s === PlayQueueState.PLAYING)
        : null

    const view = (
        <div
            className={`recommendation type-${data.type}`}
            classList={playState ? [playState] : []}
            // Native right-click context menu mirrors GH's web behavior:
            // the desktop pointer affordance is "right click on the
            // card to open the action sheet" — there's no inline more
            // button on the tile itself.
            oncontextmenu={(event) => {
                event.preventDefault()
                openRecommendationOverflow({
                    data,
                    event,
                    anchor: event.currentTarget as HTMLElement,
                })
            }}
        >
            <button className="cardClickArea" onclick={navigate}>
                <ImageView
                    image={resizedImage}
                    placeholder={<ImagePlaceholder />}
                    loadWhenIdle={true}
                />
                <div className="details">
                    <div className={'type'}>{data.type}</div>
                    <div className={'title'}>{data.title}</div>
                    {data.artistName ? <div className={'artistName'}>{data.artistName}</div> : null}
                </div>
            </button>
            {/*
              Active-queue equalizer overlay — sits over the artwork
              and reveals when the card matches the active queue. Uses
              the same animated bars as TrackRenderer; CSS gates
              visibility on the recommendation's playing/paused class.
            */}
            <span className="recommendationPlayingOverlay" aria-hidden="true">
                <span className="bar" />
                <span className="bar" />
                <span className="bar" />
            </span>
            {isPlayable ? (
                <button
                    className="playOverlay"
                    aria-label={
                        isCurrentlyPlaying
                            ? isCurrentlyPlaying.map((p) =>
                                  p ? `Pause ${data.title}` : `Play ${data.title}`
                              )
                            : `Play ${data.title}`
                    }
                    onclick={play}
                >
                    <Icon
                        icon={
                            isCurrentlyPlaying
                                ? isCurrentlyPlaying.map((p) => (p ? 'pause' : 'play'))
                                : 'play'
                        }
                    />
                </button>
            ) : null}
        </div>
    )
    return view
}

/**
 * Sets a music-video recommendation as a single-track queue
 * synchronously, so the stage shows artwork + title the instant the
 * card is clicked. The PRS round-trip happens lazily inside the
 * playlist entry's `trackLoadOptions` thunk — the player binding
 * resolves it when actually loading.
 */
function playVideoFromRecommendation(
    data: RecommendationModel,
    deps: RecommendationRendererDeps
): Promise<void> {
    if (!data.id) return Promise.resolve()
    // PRS client pulls in DRM helpers; lazy-import inside the thunk
    // so the recommendation bundle stays small for the audio-only
    // path. The thunk runs once when vinyl asks for the load
    // options — same network cost, but the UI is up before then.
    const trackId = data.id
    return import('@/video/videoQueue').then(({ setVideoQueue }) =>
        setVideoQueue(deps, {
            id: trackId,
            title: data.title,
            artistName: data.artistName ?? '',
            durationSec: 0,
            image: data.image,
            trackLoadOptions: async () => {
                const { resolveVideoTrack } = await import('@/video/videoPlayback')
                const resolved = await resolveVideoTrack(trackId)
                if (!resolved) {
                    throw new Error(`Could not resolve video track ${trackId}`)
                }
                return resolved.trackLoadOptions
            },
            entity: data.node,
        })
    )
}

// Map of entity types that should navigate to a detail page when clicked.
// Stations and tracks are intentionally absent — the card click should play
// them, not navigate.
const linkTypeMap = {
    Playlist: 'playlists',
    Album: 'albums',
    Artist: 'artists',
    PodcastShow: 'podcasts',
    Audiobook: 'audiobooks',
    Video: 'videos',
} as const

export interface ToRecommendationModelOptions {
    /**
     * The browse row's `itemType`. When set to `VIDEO`, Track-typed
     * items in the row are reclassified as Video — Firefly returns
     * music videos under the `Track` __typename when they sit inside
     * a video-home row, so the typename alone isn't enough to route
     * them through the video flow.
     */
    readonly rowItemType?: string | null | undefined
}

export function toRecommendationModel(
    node: EntityUnion,
    options: ToRecommendationModelOptions = {}
): RecommendationModel | null {
    const typename = node.__typename
    switch (typename) {
        case 'Album':
        case 'Artist':
        case 'Station':
        case 'Playlist':
        case 'PodcastShow':
        case 'PodcastEpisode':
        case 'Track':
        case 'Audiobook':
        case 'Video': {
            // Firefly returns music-video cards inside a `VIDEO` row as
            // Track typenames. The detail page (`video(id:)` query)
            // doesn't currently resolve a Video for those ASINs — green
            // hornet hides the rows entirely. We surface them with the
            // 16:9 video card visuals (because the row itemType still
            // governs sizing in the parent), but route the click to the
            // /videos/:id page where the player will pick the right
            // playback path once the schema gap is resolved.
            const effectiveType: RecommendationType =
                typename === 'Track' && options.rowItemType === 'VIDEO' ? 'Video' : typename
            const link =
                effectiveType in linkTypeMap
                    ? `${linkTypeMap[effectiveType as keyof typeof linkTypeMap]}/${node.id}`
                    : null

            const artistName =
                'contributingArtists' in node
                    ? (node.contributingArtists?.concatenatedName ?? null)
                    : null

            const image = getLargestImage(node.images, 'PRIMARY') ?? getLargestImage(node.images)
            if (!image || !node.playParams) return null
            const title = 'title' in node ? node.title : node.name

            return {
                id: node.id,
                type: effectiveType,
                title: title ?? '',
                artistName,
                image,
                playParams: node.playParams,
                link,
                // Video doesn't implement PlayableEntity, but the renderer
                // never queues it (Video clicks navigate to the detail page).
                // Cast through unknown for the assignment.
                node: node as unknown as PlayableEntity,
            }
        }
        default: {
            logWarn(target, 'Unknown recommendation type:', typename)
            return null
        }
    }
}
